import React, { createContext, useContext, useMemo } from "react";
import {
  Link,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { EmbeddedFlowHostContext } from "./EmbeddedFlowContext";

/**
 * A synthetic route context, supplied by `EmbeddedFlow` when a route-level page
 * is rendered inline instead of being navigated to.
 *
 * React Router v6 refuses to nest one `<Router>` inside another, so an embedded
 * page cannot be given a private `MemoryRouter`. Instead the host keeps the
 * location in state and publishes it here; pages read it through the shim hooks
 * below, which fall back to the real router whenever this context is absent.
 *
 * @type {React.Context<{location: {pathname: string, search: string, state: any},
 *   navigate: (to: string, options?: Object) => void,
 *   params: Object}|null>}
 */
const EmbeddedRouteContext = createContext(null);

/**
 * @param {Object} props
 * @param {{pathname: string, search: string, state: any}} props.location
 * @param {(to: string, options?: Object) => void} props.navigate
 * @param {Object} props.params - Path params for the matched route.
 */
export function EmbeddedRouteProvider({ location, navigate, params, children }) {
  const value = useMemo(
    () => ({ location, navigate, params }),
    [location, navigate, params]
  );
  return (
    <EmbeddedRouteContext.Provider value={value}>
      {children}
    </EmbeddedRouteContext.Provider>
  );
}

/** True when the caller is rendering inside an embedded flow. */
export function useIsEmbeddedRoute() {
  return useContext(EmbeddedRouteContext) !== null;
}

/**
 * `useLocation` that resolves to the embedded location when hosted inline.
 *
 * Pages alias this on import (`import { useRouteLocation as useLocation }`) so
 * their existing call sites are untouched.
 *
 * @returns {{pathname: string, search: string, state: any}}
 */
export function useRouteLocation() {
  const embedded = useContext(EmbeddedRouteContext);
  // Called unconditionally to satisfy the rules of hooks; the whole app sits
  // inside a BrowserRouter, so this never throws.
  const real = useLocation();
  return embedded ? embedded.location : real;
}

/**
 * The single `navigate` used by everything that can appear inside a panel.
 * Resolves in three cases, checked in this order:
 *
 *  1. **Already inside a flow** — hand to the flow, which decides whether the
 *     destination is another step (stay inline) or the exit.
 *  2. **Outside a flow but inside a surface that can host one** — open it
 *     inline rather than navigating away. This is the "Add plan" case.
 *  3. **Neither** — the real router, so `/dashboard/:id` is unaffected.
 *
 * Case 1 must be checked first: a page inside a flow has no host context of
 * its own, so without it every in-flow navigation would escape to the real
 * router and dump the coach out of the panel.
 *
 * Numeric deltas (`navigate(-1)`) always reach the real router — neither the
 * flow nor the host has a history stack to walk.
 *
 * @returns {(to: string|number, options?: Object) => void}
 */
export function useRouteNavigate() {
  const embedded = useContext(EmbeddedRouteContext);
  const host = useContext(EmbeddedFlowHostContext);
  const real = useNavigate();

  return useMemo(() => {
    if (!embedded && !host) return real;
    return (to, options) => {
      if (typeof to !== "string") {
        real(to, options);
        return;
      }
      if (embedded) {
        embedded.navigate(to, options);
        return;
      }
      if (host.canEmbed(to)) {
        host.open(to, options);
        return;
      }
      real(to, options);
    };
  }, [embedded, host, real]);
}

/**
 * Alias kept for feature tabs, which read as "open this inline if you can".
 * Same implementation — see useRouteNavigate.
 */
export const useFlowNavigate = useRouteNavigate;

/** `useParams` that resolves against the embedded route when hosted inline. */
export function useRouteParams() {
  const embedded = useContext(EmbeddedRouteContext);
  const real = useParams();
  return embedded ? embedded.params : real;
}

/**
 * `Link` that navigates within the embedded flow when hosted inline. Renders a
 * real anchor either way so middle-click and styling still behave, but
 * suppresses the default and routes through the host when embedded.
 */
export function RouteLink({ to, state, replace, children, ...rest }) {
  const embedded = useContext(EmbeddedRouteContext);

  if (!embedded) {
    return (
      <Link to={to} state={state} replace={replace} {...rest}>
        {children}
      </Link>
    );
  }

  return (
    <a
      href={to}
      onClick={(event) => {
        // Let the browser handle modified clicks (new tab / new window).
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
          return;
        }
        event.preventDefault();
        embedded.navigate(to, { state, replace });
      }}
      {...rest}
    >
      {children}
    </a>
  );
}

/**
 * Path params for a pathname against a set of route patterns.
 *
 * @param {Array<{path: string}>} routes
 * @param {string} pathname
 * @returns {Object}
 */
export function paramsForPath(routes, pathname) {
  for (const route of routes) {
    const match = matchPath(route.path, pathname);
    if (match) return match.params || {};
  }
  return {};
}
